import * as React from 'react';
import { createGetCafeService, Config } from '@cafe/core';
import { CafeProvider } from '@cafe/react';
import { useCapEnvironmentsService } from '@cengage/cap-portal-environment-lib-react';
import { useCapAuthenticationService } from '@cengage/cap-portal-authentication-lib-react';

import { useSubscribingEntity } from '../shared/subscribing-entity';

export const getCafeService = createGetCafeService();

type CafeModuleProvider = React.PropsWithChildren & {
  options: Partial<Config>;
};

export function CafeEventingModule({ children, options }: CafeModuleProvider) {
  const authenticationService = useCapAuthenticationService();
  const environmentService = useCapEnvironmentsService();
  const subscribingEntity = useSubscribingEntity();
  const eventingService = getCafeService();

  // use "insertion" effect to synchronously set user GUID
  // before any other effect (like routing) fires
  React.useInsertionEffect(() => {
    eventingService.init({
      ...options,
      productEnvironment: environmentService.environmentName,
      eventingEndpoint:
        environmentService.getEnvironmentSetting('eventingEndpoint'),
      apiKey: environmentService.getEnvironmentSetting('errorLoggingApiKey'),
    } as Config);

    const subscription = authenticationService
      .subscribeToAuthenticationNotifications()
      .subscribe((isAuthenticated) => {
        if (isAuthenticated) {
          eventingService.setGlobalContext({
            userSSOGUID: authenticationService.getUserIdentifier()!,
          });
        } else {
          eventingService.setGlobalContext({});
        }
      });

    subscribingEntity.addSubscriptionToCloseOnDestroy(subscription);
  }, []);

  return <CafeProvider value={eventingService}>{children}</CafeProvider>;
}
